<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import AddStudentList from '@/components/Dialogs/Forms/AddStudentList.vue';
import { apiRequest } from '@/api/http';
import { ADMIN_ROLE_CODES, RoleCode } from '@/constants/accounts';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { titleCase } from '@/utils/format';

// Students in one room for the selected school year, opened from Rooms.
useSessionGuard();

const route = useRoute();
const router = useRouter();
const store = useStore();

const roomId = computed(() => route.params.id);
const grade = computed(() => String(route.query.grade || ''));
const roomName = computed(() => String(route.query.name || 'Room'));
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  if (!grade.value || !year.value) return [];
  const rows = await apiRequest(
    'get',
    `/rooms-section/getRoomClassList/${roomId.value}/${encodeURIComponent(grade.value)}/${year.value}`,
  );
  return (rows || []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch([roomId, year], load, { immediate: true });

const headers = [
  { title: '#', key: 'index', sortable: false, width: 64 },
  { title: 'Name', key: 'name' },
  { title: 'LRN', key: 'lrnNo', width: 180 },
  { title: 'Sex', key: 'sex', width: 120 },
];

// "40 students · 21 male, 19 female", the totals a DepEd class list carries.
const summary = computed(() => {
  const total = `${items.value.length} student${items.value.length === 1 ? '' : 's'}`;
  if (!items.value.length) return total;
  const male = items.value.filter((s) => String(s.sex).toLowerCase() === 'male').length;
  const female = items.value.filter((s) => String(s.sex).toLowerCase() === 'female').length;
  return `${total} · ${male} male, ${female} female`;
});

// Admins add students here (the JHS admin to Grades 7-10, the SHS admin to
// Grades 11-12; the server checks it), the same way advisers do from their
// own class.
const canAddStudents = computed(() =>
  [...ADMIN_ROLE_CODES, RoleCode.Superadmin].includes(store.getters.activeRoleCode),
);
const addOpen = shallowRef(false);
const existingLrns = computed(() => items.value.map((s) => s.lrnNo).filter(Boolean));

function back() {
  router.push({ path: route.path.replace(/\/\d+\/students$/, ''), query: { grade: grade.value } });
}

function print() {
  window.open(
    `${process.env.VUE_APP_SERVER}/pdf-generator/getAllStudenList/${year.value}/${roomId.value}/${encodeURIComponent(grade.value)}`,
    '_blank',
  );
}
</script>

<template>
  <div class="page-stack">
    <div>
      <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="back">Rooms</v-btn>
    </div>

    <UiTableCard
      :title="`${grade} · ${roomName}`"
      :description="loading ? 'Loading…' : summary"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :items-per-page="25"
      search-placeholder="Search by name or LRN"
      empty-text="No students in this room for the selected school year."
    >
      <template #actions>
        <v-btn variant="outlined" prepend-icon="mdi-printer-outline" @click="print">Print</v-btn>
        <v-btn v-if="canAddStudents" color="primary" prepend-icon="mdi-account-multiple-plus-outline" @click="addOpen = true">
          Add students
        </v-btn>
      </template>
      <template #[`item.index`]="{ index }">
        <span class="text-medium-emphasis">{{ index + 1 }}</span>
      </template>
      <template #[`item.name`]="{ item }">
        <UiPerson :name="item.name" />
      </template>
      <template #[`item.lrnNo`]="{ item }">
        <span style="font-variant-numeric: tabular-nums">{{ item.lrnNo || '—' }}</span>
      </template>
      <template #[`item.sex`]="{ item }">
        <span :class="{ 'text-medium-emphasis': !item.sex }">{{ item.sex || '—' }}</span>
      </template>
    </UiTableCard>

    <AddStudentList
      v-if="canAddStudents"
      v-model="addOpen"
      :room-id="Number(roomId)"
      :room-name="roomName"
      :grade-level="grade"
      :school-year-id="year"
      :existing-lrns="existingLrns"
      @saved="load"
    />
  </div>
</template>
